// HousekeepingStaffMaster.js  CRUD for housekeeping staff.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Chip, Snackbar, Alert,
  CircularProgress, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { housekeepingApi, ctx } from './modulesApi';

const EMPTY = {
  staffId: 0, companyId: 0, hotelId: 0, staffName: '',
  gender: '', contact: '', shift: 'Morning', availabilityStatus: 'Available', isActive: true,
};
const SHIFTS = ['Morning', 'Evening', 'Night'];
const AVAIL = ['Available', 'Busy', 'OnLeave'];
const GENDERS = ['Male', 'Female', 'Other'];

export default function HousekeepingStaffMaster() {
  const { companyId, hotelId, userId } = ctx();
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(''); const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false); const [form, setForm] = useState(EMPTY);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await housekeepingApi.listStaff(hotelId, { page: page + 1, pageSize: rowsPerPage, search });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const onSave = async () => {
    if (!form.staffName) return notify('Name required', 'warning');
    try {
      await housekeepingApi.saveStaff({ ...form, companyId, hotelId }, userId);
      notify('Saved'); setOpen(false); setForm(EMPTY); load();
    } catch { notify('Failed', 'error'); }
  };
  const onDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await housekeepingApi.deleteStaff(id, userId); notify('Deleted'); load(); }
    catch { notify('Failed', 'error'); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <CleaningServicesIcon color='primary' />
              <Typography variant='h5'>Housekeeping Staff</Typography>
            </Box>
            <Button variant='contained' startIcon={<AddIcon />}
              onClick={() => { setForm({ ...EMPTY, companyId, hotelId }); setOpen(true); }}>Add Staff</Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size='small' label='Search' value={search}
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
            </Grid>
          </Grid>

          <TableContainer><Table size='small'>
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Gender</TableCell>
              <TableCell>Contact</TableCell><TableCell>Shift</TableCell>
              <TableCell>Availability</TableCell><TableCell>Status</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={7} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={7} align='center'>No staff</TableCell></TableRow>)}
              {items.map((s) => (
                <TableRow key={s.staffId} hover>
                  <TableCell>{s.staffName}</TableCell>
                  <TableCell>{s.gender}</TableCell>
                  <TableCell>{s.contact}</TableCell>
                  <TableCell>{s.shift}</TableCell>
                  <TableCell><Chip size='small' label={s.availabilityStatus}
                    color={s.availabilityStatus === 'Available' ? 'success'
                          : s.availabilityStatus === 'Busy' ? 'warning' : 'default'} /></TableCell>
                  <TableCell><Chip size='small' label={s.isActive ? 'Active' : 'Inactive'}
                    color={s.isActive ? 'success' : 'default'} /></TableCell>
                  <TableCell align='right'>
                    <IconButton size='small' onClick={() => { setForm({ ...s }); setOpen(true); }}><EditIcon /></IconButton>
                    <IconButton size='small' color='error' onClick={() => onDelete(s.staffId)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{form.staffId ? 'Edit' : 'Add'} Staff</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label='Name' value={form.staffName}
              onChange={(e) => setForm({ ...form, staffName: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Gender' value={form.gender || ''}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label='Contact' value={form.contact || ''}
              onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Shift' value={form.shift || ''}
                onChange={(e) => setForm({ ...form, shift: e.target.value })}>
                {SHIFTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label='Availability' value={form.availabilityStatus}
                onChange={(e) => setForm({ ...form, availabilityStatus: e.target.value })}>
                {AVAIL.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><FormControlLabel control={
              <Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            } label='Active' /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
